import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Store,
  MapPin,
  Search,
  Star,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StoresListPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchQuery] = useState("");

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const storeTypeLabels = {
    seller: "Vendeur",
    supplier: "Fournisseur",
    artisan: "Artisan",
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-gray-900">
              Nos Boutiques & Artisans
            </h1>
            <p className="text-gray-600 mt-2">
              Découvrez les meilleurs vendeurs et artisans près de chez vous
            </p>
          </div>

          <div className="relative w-full md:w-72">
            <Input
              placeholder="Rechercher une boutique..."
              value={searchTerm}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores.map((store) => (
              <Link to={`/boutique/${store.slug}`} key={store.id} className="block group">
                <Card className="h-full overflow-hidden hover:shadow-lg transition-all duration-300 border-transparent hover:border-orange-200">
                  <div className="h-32 bg-gray-100 relative overflow-hidden">
                    {store.banner_url ? (
                      <img
                        src={store.banner_url}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-orange-100 to-orange-50" />
                    )}
                    <div className="absolute -bottom-8 left-6">
                      <div className="w-16 h-16 rounded-xl border-4 border-white bg-white shadow-sm overflow-hidden">
                        {store.logo_url ? (
                          <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-50 text-orange-500 font-bold text-xl">
                            {store.name[0]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <CardContent className="pt-10 pb-6 px-6">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 group-hover:text-orange-600 transition-colors flex items-center gap-2">
                          {store.name}
                          {store.is_verified && (
                            <CheckCircle className="h-4 w-4 text-blue-500 bg-white rounded-full" fill="currentColor" />
                          )}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <Badge variant="secondary" className="font-normal">
                            {storeTypeLabels[store.store_type]}
                          </Badge>
                          {store.stats?.rating > 0 && (
                            <div className="flex items-center text-amber-500">
                              <Star className="h-3 w-3 fill-current" />
                              <span className="ml-1 font-medium">{store.stats.rating}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {store.description || "Aucune description disponible."}
                    </p>

                    {store.address && (
                      <div className="flex items-center text-xs text-gray-500 mb-4">
                        <MapPin className="h-3 w-3 mr-1" />
                        {store.address}
                      </div>
                    )}

                    <Button variant="outline" className="w-full group-hover:bg-orange-50 group-hover:text-orange-600 group-hover:border-orange-200">
                      Visiter la boutique
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {!loading && filteredStores.length === 0 && (
          <div className="text-center py-16">
            <Store className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Aucune boutique trouvée</h2>
            <p className="text-gray-500">Essayez une autre recherche.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
